import { useMemo } from 'react';
import { cn } from '../../lib/cn';

export interface StarfieldProps {
  /** Star count. ~120 reads as dense without hurting paint cost on phones. */
  density?: number;
  /** Parallax layers. Each drifts at a different rate. */
  layers?: number;
  /** Deterministic layout — same seed renders the same sky every time. */
  seed?: number;
  /** Adds the nebula wash behind the stars. */
  nebula?: boolean;
  className?: string;
}

/**
 * Drifting CSS star layer (GDD §4, "Im Hintergrund ziehen CSS-Partikel vorbei").
 *
 * Implemented as plain positioned divs rather than a canvas so it composites on
 * the GPU and costs nothing on the main thread — important because this sits
 * behind the whole HUD and must never compete with the game loop.
 *
 * The layout is seeded rather than random so the sky doesn't reshuffle on every
 * re-render, which would read as flickering.
 */
export function Starfield({
  density = 120,
  layers = 3,
  seed = 7,
  nebula = false,
  className,
}: StarfieldProps) {
  const stars = useMemo(() => {
    const rand = mulberry32(seed);
    return Array.from({ length: density }, () => {
      const layer = Math.floor(rand() * layers);
      return {
        top: rand() * 100,
        left: rand() * 100,
        // Far layers are smaller, dimmer and slower — that's the parallax cue.
        size: 0.5 + (layer / Math.max(1, layers - 1)) * 1.6,
        opacity: 0.25 + (layer / Math.max(1, layers - 1)) * 0.6,
        duration: 90 - layer * 22 + rand() * 20,
        delay: -rand() * 60,
      };
    });
  }, [density, layers, seed]);

  return (
    <div
      aria-hidden
      className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}
    >
      {nebula ? <div className="absolute inset-0 bg-nebula" /> : null}

      {stars.map((star, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-obsidian-50 motion-safe:animate-drift-slow"
          style={{
            top: `${star.top}%`,
            left: `${star.left}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: star.opacity,
            animationDuration: `${star.duration}s`,
            animationDelay: `${star.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

/** Small deterministic PRNG — keeps the sky stable across renders. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
